import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import AutoAwesomeIcon from "@material-ui/icons/AutoAwesome";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";

import { facturasApi } from "src/services/facturasApi";
import { AlertaModalV2 } from "../../AlertaSwall";
import { BRAND } from "src/theme/brandTokens";

const MAX_GLOSA = 80;
const MAX_DESCRIPCION = 1000;
const UMBRAL_AVISO_GLOSA = 60;

export default function DetalleSection({ tarea, formik }) {
  const { values, errors, touched, setFieldValue, handleChange, handleBlur } =
    formik;

  const [sugerirGlosa, { isLoading: cargandoIA }] =
    facturasApi.useSugerirGlosaMutation();

  const [mostrarDescripcion, setMostrarDescripcion] = useState(
    Boolean(values.descripcion),
  );
  const [glosaGeneradaPorIA, setGlosaGeneradaPorIA] = useState(false);
  const [errorIA, setErrorIA] = useState(null);

  const ultimaGlosaIA = useRef(null);

  const handleSugerirIA = async () => {
    setErrorIA(null);
    try {
      const result = await sugerirGlosa({
        idTarea: tarea.id,
        montoFacturar: Number(values.montoFacturar) || 0,
        totalPagarCLP: Number(values._meta?.totalPagarCLP) || 0,
        totalFacturadoCLP: Number(values._meta?.totalFacturadoCLP) || 0,
        esFacturaATercero: values.tipoFacturacion === "OtraEmpresa",
      }).unwrap();

      setFieldValue("glosa", result.glosa);
      ultimaGlosaIA.current = result.glosa;

      if (result.descripcion) {
        setFieldValue("descripcion", result.descripcion);
        setMostrarDescripcion(true);
      }

      setGlosaGeneradaPorIA(true);
    } catch (error) {
      setErrorIA(
        error?.data?.message ??
          "No se pudo generar la sugerencia. Intenta de nuevo.",
      );
    }
  };

  const handleChangeGlosa = (event) => {
    const nuevoValor = event.target.value;
    if (nuevoValor.length > MAX_GLOSA) return;
    if (glosaGeneradaPorIA && nuevoValor !== ultimaGlosaIA.current) {
      setGlosaGeneradaPorIA(false);
    }
    handleChange(event);
  };

  const descartarSugerencia = () => {
    setFieldValue("glosa", tarea?.nombre || "");
    setFieldValue("descripcion", "");
    setMostrarDescripcion(false);
    setGlosaGeneradaPorIA(false);
    ultimaGlosaIA.current = null;
  };

  const solicitarQuitarDescripcion = async () => {
    if (values.descripcion) {
      const result = await AlertaModalV2({
        title: "¿Quitar la descripción?",
        text: "Se perderá el texto ingresado.",
        confirmButtonText: "Sí, quitar",
        confirmButtonColor: "#d32f2f",
        icon: "warning",
      }).fire();
      if (!result.isConfirmed) return;
    }
    setFieldValue("descripcion", "");
    setMostrarDescripcion(false);
  };

  const glosaLen = (values.glosa || "").length;
  const glosaContadorColor =
    glosaLen >= UMBRAL_AVISO_GLOSA ? "#b26a00" : "text.secondary";

  const descripcionLen = (values.descripcion || "").length;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          📝 Detalle
        </Typography>

        <TextField
          label="Glosa Factura"
          name="glosa"
          value={values.glosa}
          onChange={handleChangeGlosa}
          onBlur={handleBlur}
          fullWidth
          error={touched.glosa && !!errors.glosa}
          InputProps={{
            endAdornment: (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Divider orientation="vertical" sx={{ height: 28, m: 0.5 }} />
                <Tooltip title="Sugerir glosa con IA">
                  <span>
                    <IconButton
                      onClick={handleSugerirIA}
                      disabled={cargandoIA}
                      sx={{ p: "10px", color: BRAND.color.primary }}
                      aria-label="Sugerir glosa con IA"
                    >
                      {cargandoIA ? (
                        <CircularProgress
                          size={20}
                          sx={{ color: BRAND.color.primary }}
                        />
                      ) : (
                        <AutoAwesomeIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ),
          }}
        />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 0.5, flexWrap: "wrap", gap: 1 }}
        >
          <Box>
            {glosaGeneradaPorIA && (
              <Chip
                label="Sugerido por IA"
                size="small"
                onDelete={descartarSugerencia}
                deleteIcon={<CloseIcon />}
                sx={{
                  bgcolor: "rgba(255, 149, 0, 0.12)",
                  color: "#b26a00",
                  fontWeight: 500,
                }}
              />
            )}
            {touched.glosa && errors.glosa && (
              <Typography variant="caption" sx={{ color: "error.main", ml: 1 }}>
                {errors.glosa}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" sx={{ color: glosaContadorColor }}>
            {glosaLen}/{MAX_GLOSA}
          </Typography>
        </Stack>

        {errorIA && (
          <Alert
            severity="error"
            sx={{ mt: 1.5 }}
            action={
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleSugerirIA}
                disabled={cargandoIA}
              >
                Reintentar
              </Button>
            }
            onClose={() => setErrorIA(null)}
          >
            {errorIA}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          {!mostrarDescripcion ? (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setMostrarDescripcion(true)}
            >
              Agregar descripción
            </Button>
          ) : (
            <Stack spacing={1}>
              <TextField
                label="Descripción"
                name="descripcion"
                value={values.descripcion || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.descripcion && !!errors.descripcion}
                helperText={touched.descripcion && errors.descripcion}
                fullWidth
                multiline
                rows={4}
                inputProps={{ maxLength: MAX_DESCRIPCION }}
              />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Button
                  size="small"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={solicitarQuitarDescripcion}
                >
                  Quitar descripción
                </Button>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {descripcionLen}/{MAX_DESCRIPCION}
                </Typography>
              </Stack>
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
